import { useEffect, useState } from "react";
import api from "@/lib/api";
import { timeAgo } from "@/lib/utils-ros";
import { Shield, Search, FileClock, ChevronDown, CheckCircle2, XCircle, ShieldAlert, Loader2 } from "lucide-react";
import { toast } from "sonner";

const RESULT_META = {
  success: { icon: CheckCircle2, color: "#159A68", bg: "#E7F8F1", label: "Success" },
  failure: { icon: XCircle, color: "#B42318", bg: "#FFF1F0", label: "Failure" },
  denied: { icon: ShieldAlert, color: "#C76500", bg: "#FFF3E2", label: "Denied" },
};

const SOURCE_LABEL = { ui: "UI", ask_niq: "Ask Niq", system: "System", api: "API" };
const PAGE_SIZE = 50;

function actorLabel(a) {
  return a.user_name || (a.user_id === "unknown" ? "Unknown" : a.user_id?.slice(0, 8)) || "—";
}

function targetLabel(a) {
  if (!a.entity_type) return "—";
  return a.entity_label || `${a.entity_type} ${a.entity_id?.slice(0, 8) || ""}`;
}

export default function AuditLog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const [action, setAction] = useState("");
  const [result, setResult] = useState("all");
  const [source, setSource] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  function buildParams(skip) {
    const p = new URLSearchParams();
    p.set("skip", skip);
    p.set("limit", PAGE_SIZE);
    if (action) p.set("action", action);
    if (result !== "all") p.set("result", result);
    if (source !== "all") p.set("source", source);
    if (dateFrom) p.set("date_from", new Date(`${dateFrom}T00:00:00`).toISOString());
    if (dateTo) p.set("date_to", new Date(`${dateTo}T23:59:59`).toISOString());
    return p;
  }

  async function load() {
    setLoading(true);
    setExpanded(null);
    try {
      const { data } = await api.get(`/admin/audit-log?${buildParams(0).toString()}`);
      setItems(data.items);
      setTotal(data.total);
    } catch {
      toast.error("Could not load audit log");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    setLoadingMore(true);
    try {
      const { data } = await api.get(`/admin/audit-log?${buildParams(items.length).toString()}`);
      setItems(current => [...current, ...data.items]);
    } catch {
      toast.error("Could not load more entries");
    } finally {
      setLoadingMore(false);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function clearFilters() {
    setAction(""); setResult("all"); setSource("all"); setDateFrom(""); setDateTo("");
  }

  return (
    <div className="min-h-full bg-[#F5FAFC] pb-12" data-testid="audit-page">
      <section className="bg-[#07539A] px-5 md:px-9 pt-7 pb-16 shadow-[inset_0_-1px_0_rgba(255,255,255,0.12)]">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-5">
          <div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight !text-white">Audit Log</h1>
            <p className="text-[13px] text-white/75 mt-1">Every mutation, login, share, approval, and impersonation is recorded here.</p>
          </div>
          <div className="text-left md:text-right">
            <div className="text-[10px] uppercase tracking-widest text-white/60">Entries loaded</div>
            <div className="font-display text-2xl tabular-nums !text-white">{loading ? "—" : `${items.length} / ${total}`}</div>
          </div>
        </div>
      </section>

      <div className="px-4 md:px-8 -mt-7 relative z-10 space-y-4">
        <div className="rounded-[22px] border border-[#C9DCE6] bg-white shadow-[0_12px_30px_rgba(7,83,154,0.10)] p-4 space-y-3">
          <div className="flex items-center gap-3 h-12 px-4 border border-[#BFD5E0] rounded-full bg-[#F9FCFD] focus-within:border-[#00A2D4]">
            <Search size={16} className="text-[#07539A] shrink-0" />
            <input
              value={action}
              onChange={e => setAction(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") load(); }}
              placeholder="Filter by action (e.g. lead.create, auth.login, opportunity.delete)…"
              data-testid="audit-filter"
              className="flex-1 bg-transparent outline-none text-[13px] text-[#243B53] placeholder:text-[#9AAEB8]"
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5">
            <select value={result} onChange={e => setResult(e.target.value)} data-testid="audit-result-filter"
              className="h-10 rounded-xl border border-[#B8D3DF] bg-[#F8FCFE] px-3 text-[12px] outline-none focus:border-[#00A2D4]">
              <option value="all">All results</option>
              <option value="success">Success</option>
              <option value="failure">Failure</option>
              <option value="denied">Denied</option>
            </select>
            <select value={source} onChange={e => setSource(e.target.value)} data-testid="audit-source-filter"
              className="h-10 rounded-xl border border-[#B8D3DF] bg-[#F8FCFE] px-3 text-[12px] outline-none focus:border-[#00A2D4]">
              <option value="all">All sources</option>
              <option value="ui">UI</option>
              <option value="ask_niq">Ask Niq</option>
              <option value="system">System</option>
              <option value="api">API</option>
            </select>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="audit-date-from"
              className="h-10 rounded-xl border border-[#B8D3DF] bg-[#F8FCFE] px-3 text-[12px] outline-none focus:border-[#00A2D4]" />
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="audit-date-to"
              className="h-10 rounded-xl border border-[#B8D3DF] bg-[#F8FCFE] px-3 text-[12px] outline-none focus:border-[#00A2D4]" />
            <div className="flex items-center gap-2">
              <button onClick={load} data-testid="audit-apply" className="flex-1 h-10 px-3 text-[12px] font-semibold rounded-xl bg-[#07539A] hover:bg-[#063F73] !text-white">Apply</button>
              <button onClick={() => { clearFilters(); }} className="h-10 px-3 text-[11px] text-[#607D8B] hover:text-[#07539A]">Clear</button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="bg-white border border-[#C9DCE6] rounded-[22px] text-[#78909C] p-10 text-center shadow-[0_8px_22px_rgba(7,83,154,0.06)]">Loading audit activity…</div>
        ) : items.length === 0 ? (
          <div className="text-center p-10 border border-dashed border-[#B8D3DF] rounded-[22px] bg-white text-[13px] text-[#607D8B] shadow-[0_8px_22px_rgba(7,83,154,0.04)]">
            <span className="w-11 h-11 rounded-xl bg-[#E7F5FB] text-[#07539A] flex items-center justify-center mx-auto mb-3"><FileClock size={20} /></span>
            No audit entries match your filter.
          </div>
        ) : (
          <div className="border border-[#C9DCE6] rounded-[22px] overflow-hidden bg-white shadow-[0_10px_28px_rgba(7,83,154,0.08)]">
            <div className="overflow-x-auto">
              <div className="min-w-[860px]">
                <div className="grid grid-cols-[130px_150px_minmax(160px,1.3fr)_minmax(160px,1.3fr)_100px] gap-3 px-4 py-3 bg-[#F3FAFD] border-b border-[#D7E8F0] text-[10px] font-semibold text-[#07539A]">
                  <div>Time</div>
                  <div>Actor</div>
                  <div>Action</div>
                  <div>Target</div>
                  <div className="text-center">Result</div>
                </div>
                <div className="divide-y divide-[#E1EBF0]">
                  {items.map(a => {
                    const meta = RESULT_META[a.result] || RESULT_META.success;
                    const isOpen = expanded === a.id;
                    const hasDetail = a.ip || a.reason || (a.changes && Object.keys(a.changes).length > 0) || (a.metadata && Object.keys(a.metadata).length > 0) || a.session_id;
                    return (
                      <div key={a.id} data-testid={`audit-${a.id}`}>
                        <div
                          onClick={() => hasDetail && setExpanded(isOpen ? null : a.id)}
                          className={`grid grid-cols-[130px_150px_minmax(160px,1.3fr)_minmax(160px,1.3fr)_100px] gap-3 items-center px-4 py-3 ${hasDetail ? "cursor-pointer hover:bg-[#F8FCFE]" : ""} transition-colors`}
                        >
                          <div className="text-[11px] text-[#78909C]" title={a.created_at}>{timeAgo(a.created_at)}</div>
                          <div className="text-[12px] text-[#455A64] truncate">
                            {actorLabel(a)}
                            <span className="block text-[9px] text-[#90A4AE] uppercase tracking-wider">{SOURCE_LABEL[a.source] || a.source}</span>
                          </div>
                          <div className="font-mono text-[11px] font-semibold text-[#07539A] truncate">{a.action}</div>
                          <div className="text-[12px] text-[#607D8B] truncate">{targetLabel(a)}</div>
                          <div className="flex items-center justify-center gap-1.5">
                            <span className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full"
                              style={{ background: meta.bg, color: meta.color }}>
                              <meta.icon size={10} /> {meta.label}
                            </span>
                            {hasDetail && <ChevronDown size={13} className={`text-[#90A4AE] transition-transform ${isOpen ? "rotate-180" : ""}`} />}
                          </div>
                        </div>
                        {isOpen && (
                          <div className="px-4 pb-4 -mt-1 bg-[#F8FCFE] border-t border-[#E1EBF0]">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 text-[11px]">
                              <div><span className="text-[#90A4AE] uppercase tracking-wider text-[9px] block mb-0.5">IP address</span>{a.ip || "—"}</div>
                              <div><span className="text-[#90A4AE] uppercase tracking-wider text-[9px] block mb-0.5">Full timestamp (UTC)</span>{a.created_at}</div>
                              {a.reason && (
                                <div className="md:col-span-2"><span className="text-[#90A4AE] uppercase tracking-wider text-[9px] block mb-0.5">Reason</span>{a.reason}</div>
                              )}
                              {a.session_id && (
                                <div><span className="text-[#90A4AE] uppercase tracking-wider text-[9px] block mb-0.5">Session</span><span className="font-mono">{a.session_id}</span></div>
                              )}
                              {a.changes && Object.keys(a.changes).length > 0 && (
                                <div className="md:col-span-2">
                                  <span className="text-[#90A4AE] uppercase tracking-wider text-[9px] block mb-1">Changes</span>
                                  <div className="space-y-1">
                                    {Object.entries(a.changes).map(([field, diff]) => (
                                      <div key={field} className="font-mono text-[10px] bg-white border border-[#DCEAF0] rounded-lg px-2.5 py-1.5">
                                        <span className="text-[#07539A]">{field}</span>: {JSON.stringify(diff.from)} → {JSON.stringify(diff.to)}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {a.metadata && Object.keys(a.metadata).length > 0 && (
                                <div className="md:col-span-2">
                                  <span className="text-[#90A4AE] uppercase tracking-wider text-[9px] block mb-1">Metadata</span>
                                  <div className="font-mono text-[10px] break-all bg-white border border-[#DCEAF0] rounded-lg px-2.5 py-1.5">{JSON.stringify(a.metadata)}</div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            {items.length < total && (
              <div className="p-4 border-t border-[#E1EBF0] flex justify-center">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  data-testid="audit-load-more"
                  className="h-9 px-5 rounded-xl border border-[#BFD6E1] bg-white hover:border-[#07539A] hover:text-[#07539A] text-[12px] font-medium flex items-center gap-2 disabled:opacity-50"
                >
                  {loadingMore ? <Loader2 size={13} className="animate-spin" /> : <Shield size={13} />}
                  Load more ({total - items.length} remaining)
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
